import { useLocation } from "@tanstack/react-router";
import { useSidebarStore } from "@/components/nav/sidebar-store";
import {
  getDomainFromPath,
  matchEntityPattern,
  domainSidebars,
  noDomainSidebarPaths,
} from "./config";
import type {
  DomainId,
  DomainSidebarConfig,
  EntityPattern,
  NavigationMode,
} from "./types";

export interface NavigationModeResult {
  /** Current navigation mode */
  mode: NavigationMode;
  /** Current domain (for browse mode rail highlighting) */
  currentDomain: DomainId | null;
  /** Domain sidebar config (for browse mode) */
  domainSidebar: DomainSidebarConfig | null;
  /** Matched entity pattern (for entity mode) */
  entityMatch: EntityPattern | null;
}

/**
 * Hook to determine the current navigation mode based on:
 * 1. Whether a sidebar config is set (entity pages set their own config)
 * 2. URL pattern matching against entity patterns
 *
 * Returns the current mode and relevant context for rendering the sidebar.
 *
 * Note: domainSidebar is always provided when available, even in entity mode.
 * This allows the sidebar to fall back to domain navigation when an entity
 * page doesn't explicitly set useSidebarConfig.
 */
export function useNavigationMode(): NavigationModeResult {
  const location = useLocation();
  const { config } = useSidebarStore();
  const pathname = location.pathname;

  // Determine current domain from path
  const currentDomain = getDomainFromPath(pathname);

  // Always get domain sidebar config (used as fallback in entity mode)
  // Suppress it for fullscreen views that have their own internal navigation
  const domainSidebar =
    noDomainSidebarPaths.some((p) => pathname.startsWith(p)) || !currentDomain
      ? null
      : (domainSidebars[currentDomain] ?? null);

  // If a sidebar config is set by a route, we're in entity mode
  if (config) {
    return {
      mode: "entity",
      currentDomain,
      domainSidebar, // Provided for potential fallback use
      entityMatch: null,
    };
  }

  // Check if current path matches an entity pattern
  const entityMatch = matchEntityPattern(pathname);
  if (entityMatch) {
    return {
      mode: "entity",
      currentDomain,
      domainSidebar, // Provided as fallback when no useSidebarConfig is set
      entityMatch,
    };
  }

  // Browse mode
  return {
    mode: "browse",
    currentDomain,
    domainSidebar,
    entityMatch: null,
  };
}
